import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { getConfig } from '../config';
import type { CronJobStatus, HTTPCronJob, CreateCronJobRequest, UpdateCronJobRequest } from '../models/CronJob';

export enum CronJobFilterStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export interface CronJobResponse {
  id: string;
  name: string;
  cronExpression: string;
  command: any;
  status: CronJobStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListCronJobsParams {
  status: CronJobFilterStatus;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface ListCronJobsResponse {
  cronJobs: HTTPCronJob[];
  total: number;
  lastEvaluatedKey?: string;
}

export class CronJobClient {
  private readonly headers: Record<string, string>;

  constructor(private readonly apiUrl: string) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': getConfig().apiKey,
    };
  }

  private transformCronJobResponse(response: CronJobResponse): HTTPCronJob {
    const cronJob: HTTPCronJob = {
      id: response.id,
      name: response.name,
      cronExpression: response.cronExpression,
      command: response.command,
      status: response.status,
    };

    if (response.createdAt) {
      cronJob.createdAt = new Date(response.createdAt);
    }

    if (response.updatedAt) {
      cronJob.updatedAt = new Date(response.updatedAt);
    }

    return cronJob;
  }

  private transformCronJobRequest(cronJob: CreateCronJobRequest | UpdateCronJobRequest): any {
    const request: any = {};

    if ('name' in cronJob && cronJob.name !== undefined) {
      request.name = cronJob.name;
    }
    if ('cronExpression' in cronJob && cronJob.cronExpression !== undefined) {
      request.cronExpression = cronJob.cronExpression;
    }
    if ('command' in cronJob && cronJob.command !== undefined) {
      request.command = cronJob.command;
    }
    if ('status' in cronJob && cronJob.status !== undefined) {
      request.status = cronJob.status;
    }

    return request;
  }

  public async createCronJob(request: CreateCronJobRequest): Promise<HTTPCronJob> {
    try {
      const response: AxiosResponse<CronJobResponse> = await axios.post(
        `${this.apiUrl}/cronJobs`,
        this.transformCronJobRequest(request),
        {
          headers: this.headers,
        },
      );

      return this.transformCronJobResponse(response.data);
    } catch (error) {
      console.error('Error creating cron job:', error);
      throw error;
    }
  }

  public async listCronJobs(params: ListCronJobsParams): Promise<ListCronJobsResponse> {
    try {
      const queryParams = new URLSearchParams();

      // status is required according to the API spec
      if (params.status && Object.values(CronJobFilterStatus).includes(params.status)) {
        queryParams.append('status', params.status);
      } else {
        throw new Error('Status parameter is required');
      }

      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.lastEvaluatedKey) {
        queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
      }

      const url = `${this.apiUrl}/cronJobs?${queryParams.toString()}`;
      const response: AxiosResponse<{
        cronJobs: CronJobResponse[];
        total: number;
        lastEvaluatedKey?: string;
      }> = await axios.get(url, {
        headers: this.headers,
      });

      const result: ListCronJobsResponse = {
        cronJobs: response.data.cronJobs.map((cronJob) => this.transformCronJobResponse(cronJob)),
        total: response.data.total,
      };

      if (response.data.lastEvaluatedKey) {
        result.lastEvaluatedKey = response.data.lastEvaluatedKey;
      }

      return result;
    } catch (error) {
      console.error('Error listing cron jobs:', error);
      throw error;
    }
  }

  public async getCronJob(id: string): Promise<HTTPCronJob | null> {
    try {
      const response: AxiosResponse<CronJobResponse> = await axios.get(`${this.apiUrl}/cronJobs/${id}`, {
        headers: this.headers,
      });

      return this.transformCronJobResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error getting cron job:', error);
      throw error;
    }
  }

  public async updateCronJob(id: string, request: UpdateCronJobRequest): Promise<HTTPCronJob | null> {
    try {
      const response: AxiosResponse<CronJobResponse> = await axios.put(
        `${this.apiUrl}/cronJobs/${id}`,
        this.transformCronJobRequest(request),
        {
          headers: this.headers,
        },
      );

      return this.transformCronJobResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error updating cron job:', error);
      throw error;
    }
  }

  public async updateCronJobStatus(id: string, status: CronJobStatus): Promise<HTTPCronJob | null> {
    try {
      const response: AxiosResponse<CronJobResponse> = await axios.patch(
        `${this.apiUrl}/cronJobs/${id}/status`,
        { status },
        {
          headers: this.headers,
        },
      );

      return this.transformCronJobResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error updating cron job status:', error);
      throw error;
    }
  }

  public async deleteCronJob(id: string): Promise<boolean> {
    try {
      await axios.delete(`${this.apiUrl}/cronJobs/${id}`, {
        headers: this.headers,
      });

      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      console.error('Error deleting cron job:', error);
      throw error;
    }
  }
}
